import { Icon, StatusIconProtoProps } from '@nike/eds';
import { useI18nContext } from '../../../../context/language/use-i18n-context';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { ts } from '../i18n';
import './bulk-upload.css';
import { ProcessPaste } from '../type';
import { PassPolicy } from '../../../../models/IPassPolicy';
import { AppConfig } from '../../../../utils/constants';
import { VendorInfo } from '../../../../components/shared/type';

export const VendorBulkUpload: FC<{
  passPolicy: PassPolicy;
  onVendorAdd: (values: VendorInfo[]) => void;
  setSnackBar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
}> = ({ passPolicy, onVendorAdd, setSnackBar }) => {
  const [processPaste, setProcessPaste] = useState<ProcessPaste>({
    textToProcess: '',
    hasError: false,
    errorRow: '',
    errorString: '',
    ProcessedArr: [],
    ProcessObj: {},
    testRows: [],
  });
  const { i18nString } = useI18nContext();
  const [rowNumber, setRowNumber] = useState<number>(0);
  const handlePasteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let textToProcess = event.target.value;
    const vendorList: VendorInfo[] = [];
    let hasError = false;
    let errorRow = '';
    let errorString = '';
    setProcessPaste(prevState => ({ ...prevState, textToProcess }));
    try {
      if (!textToProcess.includes('\t')) {
        hasError = true;
        errorRow = ts.BULK_UPLOAD_ROWS_ERROR.stringKey;
        errorString = ts.BULK_UPLOAD_STRING_ERROR.stringKey;
        textToProcess = '';
        return;
      }

      const rows = textToProcess.split('\n');
      for (let i = 0; i < rows.length; i++) {
        const cols = rows[i].split('\t').map(col => col.trim());
        if (!cols[0] && !cols[1]) break;
        setRowNumber(i + 1);
        if (
          (passPolicy.emailFlag >= 2 && cols.length < 3) ||
          (passPolicy.emailFlag < 2 && cols.length < 2)
        ) {
          hasError = true;
          errorRow = ts.BULK_UPLOAD_ROW_ERROR.stringKey;
          errorString =
            passPolicy.emailFlag >= 2
              ? ts.BULK_UPLOAD_NAME_EMAIL_ERROR.stringKey
              : ts.BULK_UPLOAD_NAME_ERROR.stringKey;
          break;
        }

        if (cols[0] === 'First Name' && cols[1] === 'Last Name' && cols[2] === 'Email') {
          continue;
        }

        if (!cols[0]) {
          hasError = true;
          errorRow = ts.BULK_UPLOAD_ROW_ERROR.stringKey;
          errorString = ts.BULK_UPLOAD_FIRST_NAME_ERROR.stringKey;
          break;
        }

        if (!cols[1]) {
          hasError = true;
          errorRow = ts.BULK_UPLOAD_ROW_ERROR.stringKey;
          errorString = ts.BULK_UPLOAD_LAST_NAME_ERROR.stringKey;
          break;
        }

        if (passPolicy.emailFlag >= 2 && !AppConfig.regexPatterns.regexEmail.test(cols[2] || '')) {
          hasError = true;
          errorRow = ts.BULK_UPLOAD_ROW_ERROR.stringKey;
          errorString = ts.BULK_UPLOAD_EMAIL_ERROR.stringKey;
          break;
        }

        if (isDuplicateRow(rows[i], vendorList)) {
          hasError = true;
          errorRow = ts.BULK_UPLOAD_ROW_ERROR.stringKey;
          errorString = ts.BULK_UPLOAD_DUP_ERROR.stringKey;
          break;
        }

        const firstName = cols[0].replace(/[\\#,+()$~%:*?<>{}]/g, '');
        const lastName = cols[1].replace(/[\\#,+()$~%:*?<>{}]/g, '');
        const emailAddress = cols[2] || '';

        if (firstName && lastName) {
          vendorList.push({
            firstName: firstName,
            lastName: lastName,
            email: emailAddress,
            weChatId: null,
          });
        } else {
          hasError = true;
          errorRow = ts.BULK_UPLOAD_ROW_ERROR.stringKey;
          errorString = ts.BULK_UPLOAD_INVALID_ERROR.stringKey;
          break;
        }
      }
      setProcessPaste(prevState => ({
        ...prevState,
        hasError: hasError,
        errorRow: errorRow,
        errorString: errorString,
        textToProcess: textToProcess,
      }));
      if (hasError === false) {
        onVendorAdd(vendorList);
      }
    } catch (e) {
      setSnackBar({
        showSnack: true,
        message: 'Error occurred while processing',
        snackStatus: 'error',
      });
    }
  };
  const isDuplicateRow = (val: string, vendorList: VendorInfo[]) => {
    const cols = val.split('\t').map(col => col.trim());
    for (let i = 0; i < vendorList.length; i++) {
      if (
        vendorList[i].firstName === cols[0] &&
        vendorList[i].lastName === cols[1] &&
        vendorList[i].email === cols[2]
      ) {
        return true;
      }
    }
    return false;
  };
  return (
    <>
      <div className="eds-flex eds-flex--wrap eds-flex--direction-row eds-flex--justify-content-space-between  bulk-upload">
        {processPaste?.hasError && (
          <>
            <div
              className="eds-flex eds-flex--align-items-center error__message"
              data-testid="error-message"
            >
              <Icon
                name="AlertCircleFilled"
                className="eds-flex-inline eds-color-red error__icon"
              />
              <p className="eds-color-red error__text" style={{ textTransform: 'uppercase' }}>
                {i18nString(ts[processPaste?.errorRow as keyof typeof ts]).replace(
                  '{{rowNumber}}',
                  rowNumber.toString(),
                )}
              </p>
            </div>
            <div className="eds-flex eds-flex--align-items-center error__message">
              <Icon name="AlertCircleFilled" className="eds-flex-inlineeds-color-red error__icon" />
              <p className="eds-color-red error__text" style={{ textTransform: 'uppercase' }}>
                {i18nString(ts[processPaste?.errorString as keyof typeof ts])}
              </p>
            </div>
          </>
        )}

        <div className="bulk-upload__instructions">
          <h4>{i18nString(ts.BULK_UPLOAD_INSTRUCTIONS)}</h4>
          <div className="upload-example">
            <div className="eds-flex eds-flex--justify-content-flex-start eds-flex--align-items-center upload-example__row">
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column"></span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_COL_HEADER_A)}
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_COL_HEADER_B)}
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_COL_HEADER_C)}
              </span>
            </div>
            <div className="eds-flex eds-flex--justify-content-flex-start eds-flex--align-items-center upload-example__row">
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                1
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_DATA_1_1)}
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_DATA_1_2)}
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_DATA_1_3)}
              </span>
            </div>
            <div className="eds-flex eds-flex--justify-content-flex-start eds-flex--align-items-center upload-example__row">
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                2
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_DATA_2_1)}
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_DATA_2_2)}
              </span>
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-example__column">
                {i18nString(ts.BULK_UPLOAD_DATA_2_3)}
              </span>
            </div>
          </div>
          <ul className="upload-steps__list">
            <li className="eds-flex upload-steps__list-item">
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-steps__step-number">
                1
              </span>
              <span className="eds-flex upload-steps__step-text">
                <h4>{i18nString(ts.BULK_UPLOAD_FORMAT)}</h4>
                <p>{i18nString(ts.BULK_UPLOAD_FORMAT_DIRECTIONS)}</p>
              </span>
            </li>
            <li className="eds-flex upload-steps__list-item">
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-steps__step-number">
                2
              </span>
              <h4>{i18nString(ts.BULK_UPLOAD_COPY)}</h4>
            </li>
            <li className="eds-flex upload-steps__list-item">
              <span className="eds-flex-inline eds-flex--justify-content-center eds-flex--align-items-center upload-steps__step-number">
                3
              </span>
              <span className="eds-flex upload-steps__step-text">
                <h4>{i18nString(ts.BULK_UPLOAD_PASTE)}</h4>
              </span>
            </li>
          </ul>
        </div>
        <div className="bulk-upload__upload">
          <h4>{i18nString(ts.BULK_UPLOAD_LIST_HEADER)}</h4>
          <textarea
            className="upload__drop-area"
            placeholder={i18nString(ts.PLACEHOLDER_BULK_UPLOAD)}
            value={processPaste.textToProcess}
            onChange={handlePasteChange}
            data-testid="bulk-upload"
            name="ta1"
            id="ta1"
          ></textarea>
        </div>
      </div>
    </>
  );
};
